import { Switch, Route, Redirect, useLocation } from "wouter";
import { Toaster } from "./components/ui/toaster";
import { TooltipProvider } from "./components/ui/tooltip";
import { LayoutProvider } from "./context/LayoutContext";
import { AppShell } from "./components/layout/AppShell";
import { Suspense, lazy } from "react";
import { Loader } from "lucide-react";
import ProtectedRoute from "./utils/ProtectedRoute";
import MissedCheckoutPopup from "./components/common/MissedCheckoutPopup";
import AuthExpiryWatcher from "./components/common/AuthExpiryWatcher";
import ProcessingOverlay from "./components/common/ProcessingOverlay";
import SvgComponent from "./components/ui/SvgComponent";
import { PUBLIC_PATHS } from "./utils/publicPaths";


const AppsPage = lazy(() => import("./pages/AppsPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const HrDashboard = lazy(() => import("./pages/hrms/hr/HrDashboard"))
const LeavesPage = lazy(() => import("./pages/hrms/leave/Leave"));
const Advance = lazy(() => import("./pages/hrms/advances/Advance"));
const Users = lazy(() => import("./pages/hrms/users/Users"));
const Claims = lazy(() => import("./pages/hrms/claims/Claims"));
const Attendance = lazy(() => import("./pages/hrms/attendance/Attendance"));
const Task = lazy(() => import("./pages/hrms/task/Tasks"));
const Onboarding = lazy(() => import("./pages/hrms/onboarding/Onboarding"));
const OnboardingHome = lazy(() => import("./pages/hrms/onboarding/OnboardingHome"));
const ActivateAccount = lazy(() => import("./pages/hrms/onboarding/ActivateAccount"));
const OnboardingRegistration = lazy(() => import("./pages/hrms/onboarding/OnboardingRegistration"));
const TeamLeadApprovals = lazy(() => import("./pages/hrms/onboarding/TeamLeadApprovals"));
const HrApprovals = lazy(() => import("./pages/hrms/onboarding/HrApprovals"));
const DSR = lazy(() => import("./pages/hrms/dsr1/Dsr"));
// const DSR = lazy(() => import("./pages/hrms/dsr"))
const LoginPage = lazy(() => import("./LoginPage"));
const ForgotPassword = lazy(() => import("./pages/auth/ForgotPassword"));
const RestorePassword = lazy(() => import("./pages/auth/RestorePassword"));
const NotFound = lazy(() => import("./pages/not-found"));
const Clients = lazy(() => import("./pages/crm/clients/Clients"));
const Potentials = lazy(() => import("./pages/crm/potentials/Potentials"));
const Entity = lazy(() => import("./pages/crm/entity/EntityPage"));
const CRMDashboard = lazy (() => import("./pages/crm/Dashboard"))
const HelpCenter = lazy(() => import("./pages/helpcenter/HelpCenter"));


const Profile = lazy(() => import("../../hrms/src/pages/hrms/ProfileSettings/Profile"));


// Branded full-screen loader shown while a public route chunk is downloading
// (e.g. on a hard refresh of the browser).
const PublicRouteFallback = () => (
  <div className="fixed inset-0 z-[50] bg-[#f5f5f5] flex flex-col items-center justify-center gap-8">
    <div className="w-52 max-w-full px-6">
      <SvgComponent className="w-full h-auto" />
    </div>
    <div className="flex items-center gap-2 text-sm text-[#3c3c3c]/60">
      <Loader className="h-5 w-5 animate-spin text-[#3700ff]" />
      <span>Loading…</span>
    </div>
  </div>
);

// Loader used inside the app shell while an authed route chunk is downloading.
const AppRouteFallback = () => (
  <div className="flex items-center justify-center min-h-[60vh]">
    <Loader className="h-8 w-8 animate-spin text-blue-600" />
  </div>
);

function PublicRouter() {
  return (
    <Suspense fallback={<PublicRouteFallback />}>
      <Switch>
        <Route path="/">
          <Redirect to="/auth/login" />
        </Route>
        <Route path="/auth/login" component={LoginPage} />
        <Route path="/auth/register" component={OnboardingRegistration} />
        <Route path="/auth/activate-account" component={ActivateAccount} />
        <Route path="/auth/forgot-password" component={ForgotPassword} />
        <Route path="/auth/restore-password" component={RestorePassword} />
        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function AppRouter() {
  return (
    <Suspense fallback={<AppRouteFallback />}>
      <Switch>
        <Route path="/app" component={AppsPage} />

        {/* HRMS */}
        <Route path="/app/hrms/dashboard" component={Dashboard} />
        <Route path="/app/hrms/leave" component={LeavesPage} />
        <Route path="/app/hrms/advance" component={Advance} />
        <Route path="/app/hrms/claims" component={Claims} />
        <Route path="/app/hrms/attendance" component={Attendance} />
        <Route path="/app/hrms/task-management" component={Task} />
        <Route path="/app/hrms/dsr" component={DSR} />
        <Route path="/app/hrms/onboarding/requests" component={OnboardingHome} />
        <Route path="/app/hrms/onboarding/new" component={Onboarding} />
        <Route path="/app/hrms/onboarding/tl-approvals" component={TeamLeadApprovals} />
        <Route path="/app/hrms/onboarding/hr-approvals" component={HrApprovals} />
        <Route path="/app/hrms/onboarding" component={OnboardingHome} />

        {/* HR (management) */}
        <Route path="/app/hr/dashboard" component={HrDashboard} />
        <Route path="/app/hr/dashboard/:module" component={HrDashboard} />
        <Route path="/app/hr/people" component={Users} />

        {/* CRM */}
        <Route path="/app/crm/dashboard" component={CRMDashboard} />
        <Route path="/app/crm/clients">
          <ProtectedRoute>
            <Clients />
          </ProtectedRoute>
        </Route>
        <Route path="/app/crm/potentials">
          <ProtectedRoute>
            <Potentials />
          </ProtectedRoute>
        </Route>
        <Route path="/app/crm/entities">
          <ProtectedRoute>
            <Entity />
          </ProtectedRoute>
        </Route>

        {/* Cross-app */}
        <Route path="/app/profile" component={Profile} />
        <Route path="/app/help-center" component={HelpCenter} />
        <Route path="/app/help-center/:articleId" component={HelpCenter} />

        <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function App() {
  const [location] = useLocation();
  const isPublicPath = PUBLIC_PATHS.some(
    (path) => location === path || location.startsWith(`${path}/`)
  );

  return (
    <LayoutProvider>
      <TooltipProvider>
        {isPublicPath ? (
          <PublicRouter />
        ) : (
          <AppShell>
            <AppRouter />
          </AppShell>
        )}

        {/* Global Missed Checkout Popup (all pages) */}
        <MissedCheckoutPopup />

        {/* Auto-logout + redirect to login when the JWT expires */}
        <AuthExpiryWatcher />

        {/* Global Toast */}
        <Toaster />

        {/* Global processing lock: blocks the page (not the browser) while a
            submit / export / approve is in flight - see
            components/common/ProcessingOverlay.jsx and utils/processingLock.js */}
        <ProcessingOverlay />
      </TooltipProvider>
    </LayoutProvider>
  );
}

export default App;
